if (!customElements.get('cart-attributes-root')) {
  customElements.define(
    'cart-attributes-root',
    class CartAttributesRoot extends HTMLElement {
      static communesPromise;

      invoiceAttributeKeys = [
        'RUT',
        'Giro',
        'Razon social',
        'Direccion factura',
        'Region factura',
        'Comuna factura',
        'Email factura',
      ];

      connectedCallback() {
        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('input', debounce(this.handleInput.bind(this), ON_CHANGE_DEBOUNCE_TIMER));

        this.loadCommunes().then(() => {
          this.populateRegionSelects();
          this.syncInvoiceVisibility();
          this.updateShippingEstimate();
        });
      }

      async loadCommunes() {
        if (CartAttributesRoot.communesPromise) return CartAttributesRoot.communesPromise;

        const communesUrl = this.dataset.communesUrl;
        if (!communesUrl) {
          CartAttributesRoot.communesPromise = Promise.resolve([]);
          return CartAttributesRoot.communesPromise;
        }

        CartAttributesRoot.communesPromise = fetch(communesUrl)
          .then((response) => response.json())
          .then((payload) => payload.regions || [])
          .catch(() => []);

        return CartAttributesRoot.communesPromise;
      }

      handleChange(event) {
        if (!event.target.name || !event.target.name.startsWith('attributes[')) return;

        if (event.target.matches('[data-region-select]')) {
          const communeSelect = event.target
            .closest('.chile-cart-module')
            ?.querySelector('[data-commune-select]');
          if (communeSelect) {
            communeSelect.value = '';
            communeSelect.dataset.selectedValue = '';
          }
          this.populateCommuneSelect(event.target);
        }

        this.syncInvoiceVisibility();
        this.updateShippingEstimate();
        this.saveAttributes(event);
      }

      handleInput(event) {
        if (!event.target.name || !event.target.name.startsWith('attributes[')) return;
        this.updateShippingEstimate();
        this.saveAttributes(event);
      }

      populateRegionSelects() {
        this.loadCommunes().then((regions) => {
          this.querySelectorAll('[data-region-select]').forEach((select) => {
            const selectedValue = select.dataset.selectedValue || '';
            const placeholder = select.dataset.placeholder || 'Selecciona una región';

            if (select.dataset.initialized !== 'true') {
              select.innerHTML = '';
              select.append(this.buildOption('', placeholder));
              regions.forEach((region) => {
                select.append(this.buildOption(region.name, region.name));
              });
              select.dataset.initialized = 'true';
            }

            select.value = selectedValue;
            this.populateCommuneSelect(select);
          });
        });
      }

      populateCommuneSelect(regionSelect) {
        const wrapper = regionSelect.closest('.chile-cart-module');
        const communeSelect = wrapper?.querySelector('[data-commune-select]');
        if (!communeSelect) return;

        this.loadCommunes().then((regions) => {
          const regionName = regionSelect.value;
          const selectedCommune = communeSelect.value || communeSelect.dataset.selectedValue || '';
          const placeholder = communeSelect.dataset.communePlaceholder || 'Selecciona una comuna';
          const regionData = regions.find((region) => region.name === regionName);

          communeSelect.innerHTML = '';
          communeSelect.append(this.buildOption('', placeholder));

          if (!regionData) {
            communeSelect.value = '';
            communeSelect.disabled = true;
            communeSelect.dataset.selectedValue = '';
            return;
          }

          regionData.communes.forEach((commune) => {
            communeSelect.append(this.buildOption(commune, commune));
          });

          communeSelect.disabled = false;
          communeSelect.value = selectedCommune;

          if (communeSelect.value !== selectedCommune) {
            communeSelect.value = '';
            communeSelect.dataset.selectedValue = '';
          }
        });
      }

      buildOption(value, label) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        return option;
      }

      syncInvoiceVisibility() {
        const documentType = this.querySelector('[data-document-type] input:checked')?.value || '';
        const invoiceFields = this.querySelector('[data-factura-fields]');
        if (!invoiceFields) return;

        const showInvoice = documentType === 'Factura';
        invoiceFields.hidden = !showInvoice;
        invoiceFields.style.display = showInvoice ? '' : 'none';

        invoiceFields.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !showInvoice;
          if (!showInvoice) {
            field.value = '';
            if (field.dataset.selectedValue !== undefined) field.dataset.selectedValue = '';
          }
        });
      }

      updateShippingEstimate() {
        const shippingModule = this.querySelector('[data-shipping-estimator]');
        if (!shippingModule) return;

        const output = shippingModule.querySelector('[data-shipping-output]');
        const region = shippingModule.querySelector('[data-shipping-region]')?.value || '';
        if (!output) return;
        if (!region) {
          output.textContent = 'Selecciona tu región para ver el envío estimado.';
          return;
        }

        const subtotalCents = Number(shippingModule.dataset.subtotalCents || 0);
        const freeThreshold = Number(shippingModule.dataset.freeThreshold || 0);
        const metroRate = Number(shippingModule.dataset.metroRate || 0);
        const nationalRate = Number(shippingModule.dataset.nationalRate || 0);
        const extremeRate = Number(shippingModule.dataset.extremeRate || 0);
        const extremeRegions = [
          'Región de Arica y Parinacota',
          'Región de Tarapacá',
          'Región de Aysén del General Carlos Ibáñez del Campo',
          'Región de Magallanes y de la Antártica Chilena',
        ];
        let shippingValue = nationalRate;

        if (region === 'Región Metropolitana de Santiago') {
          shippingValue = metroRate;
        } else if (extremeRegions.includes(region)) {
          shippingValue = extremeRate || nationalRate;
        }

        if (freeThreshold > 0 && subtotalCents >= freeThreshold * 100) {
          output.textContent = `Tu envío estimado a ${region} es gratis.`;
          return;
        }

        const formatter = new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
          maximumFractionDigits: 0,
        });

        output.textContent = `Envío estimado a ${region}: ${formatter.format(shippingValue)}.`;
      }

      saveAttributes(event) {
        const attributes = {};
        this.querySelectorAll('[name^="attributes["]').forEach((field) => {
          const key = field.name.replace(/^attributes\[|\]$/g, '');

          if (field.disabled) {
            if (this.invoiceAttributeKeys.includes(key)) attributes[key] = '';
            return;
          }

          if (field.type === 'radio') {
            if (field.checked) attributes[key] = field.value;
            return;
          }

          if (field.type === 'checkbox') {
            attributes[key] = field.checked ? field.value : '';
            return;
          }

          attributes[key] = field.value;
        });

        if (!this.querySelector('[name="attributes[Documento tributario]"]:checked')) {
          attributes['Documento tributario'] = '';
        }

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes }),
        }).then(() => CartPerformance.measureFromEvent('cart-attributes:user-action', event));
      }
    }
  );
}
